import {
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import axios from "axios";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../../Constants";

// ─── Role helpers ────────────────────────────────────────────────────────────
const ROLE = {
  COMPANY: "COMPANY",
  SUPER_SUPER_ADMIN: "SUPER_SUPER_ADMIN",
  SUPER_ADMIN: "SUPER_ADMIN",
  RESELLER: "RESELLER",
  EXECUTIVE: "EXECUTIVE",
};
const isOwnerLevel = (t) =>
  [
    ROLE.COMPANY,
    ROLE.SUPER_SUPER_ADMIN,
    ROLE.SUPER_ADMIN,
    ROLE.RESELLER,
  ].includes(t);
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});
const isPhoneMasked = (phone) => phone && /[*xX]/.test(phone);

const formatTime = (ts) =>
  new Date(ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

// ─── JWT helpers ─────────────────────────────────────────────────────────────
const decodeJwtPayload = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const getUserIdFromToken = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  return payload?.userId || payload?.id || payload?.sub || null;
};

const mapCallActivityToCallPayload = (item) => {
  const parsedStart = item.startTime ? new Date(item.startTime).getTime() : NaN;
  return {
    leadId: item.leadId,
    leadName: item.name,
    phone: item.phone,
    statusName: item.statusName,
    statusColor: item.statusColor,
    assignedToId: item.assignedToId,
    assignedToName: item.assignedToName,
    callSessionId: item.callSessionId,
    callDuration: item.callDuration,
    callStatus: item.callStatus,
    callsToday: item.callsToday,
    receivedAt: Number.isFinite(parsedStart) ? parsedStart : Date.now(),
  };
};

// ─── Shared UI Components (From Sales God) ──────────────────────────────────
const SGAvatar = ({ name = "", size = 52 }) => {
  const parts = name.trim().split(" ");
  const initials =
    parts.length >= 2
      ? parts[0][0] + parts[parts.length - 1][0]
      : (parts[0]?.[0] ?? "?");
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "#e8f4f7",
        color: "#0C4550",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: size * 0.35,
        flexShrink: 0,
        border: "2px solid #c6e4ec",
        letterSpacing: "0.04em",
      }}
    >
      {initials.toUpperCase()}
    </div>
  );
};

const SGEditableField = ({
  value,
  onSave,
  label,
  type = "text",
  className = "",
  options,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempVal, setTempVal] = useState(value || "");

  useEffect(() => {
    setTempVal(value || "");
  }, [value]);

  const handleBlurOrSubmit = () => {
    const raw = tempVal || "";
    const trimmed = raw.trim();

    if (raw.length > 0 && trimmed === "") {
      toast.error(`${label} cannot contain only spaces.`);
      setIsEditing(false);
      setTempVal(value || "");
      return;
    }

    setIsEditing(false);
    setTempVal(trimmed);
    if (trimmed !== (value || "")) {
      onSave(trimmed);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleBlurOrSubmit();
    if (e.key === "Escape") {
      setIsEditing(false);
      setTempVal(value || "");
    }
  };

  if (isEditing) {
    if (options && options.length > 0) {
      return (
        <select
          autoFocus
          className={`sg2-inline-edit-input ${className}`}
          value={tempVal}
          onChange={(e) => setTempVal(e.target.value)}
          onBlur={handleBlurOrSubmit}
          onKeyDown={handleKeyDown}
        >
          <option value="">— Select {label} —</option>
          {options.map((opt, i) => (
            <option key={i} value={opt.name || opt}>
              {opt.name || opt}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        autoFocus
        type={type}
        className={`sg2-inline-edit-input ${className}`}
        value={tempVal}
        onChange={(e) => setTempVal(e.target.value)}
        onBlur={handleBlurOrSubmit}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <span
      className={`sg2-editable-text ${className} ${!value ? "sg2-editable-empty" : ""}`}
      onClick={() => setIsEditing(true)}
      title={`Click to edit ${label}`}
    >
      {value || `Add ${label}`} <span className="sg2-edit-icon">✎</span>
    </span>
  );
};

const SGInfoRow = ({
  icon,
  label,
  value,
  field,
  onSave,
  type = "text",
  options,
}) => (
  <div className="sg2-info-row">
    <span className="sg2-info-icon">{icon}</span>
    <div className="sg2-info-content">
      <span className="sg2-info-label">{label}</span>
      {field && onSave ? (
        <SGEditableField
          value={value}
          onSave={(v) => onSave(field, v)}
          label={label}
          type={type}
          className="sg2-info-value"
          options={options}
        />
      ) : (
        <span className="sg2-info-value" title={value}>
          {value || "—"}
        </span>
      )}
    </div>
  </div>
);

const SGCommentRow = ({ value, onClick }) => {
  return (
    <div
      className="sg2-info-row sg2-info-row--comment"
      onClick={onClick}
      title="Click to edit comment"
    >
      <span className="sg2-info-icon">💬</span>
      <div className="sg2-info-content">
        <span className="sg2-info-label">Comment</span>
        <span className={`sg2-info-value sg2-comment-value ${!value ? "sg2-editable-empty" : ""}`}>
          {value || "Add Comment"}
        </span>
      </div>
    </div>
  );
};

const SGCommentModal = ({ open, value, onChange, onSave, onClose }) => {
  if (!open) return null;
  return (
    <div className="sg2-inline-overlay">
      <div className="sg2-inline-modal">
        <div className="sg2-inline-header">
          <span>Edit Comment</span>
          <button className="sg2-inline-close" onClick={onClose}>✕</button>
        </div>
        <div className="sg2-inline-body">
          <label className="sg2-inline-label">Comment</label>
          <textarea
            autoFocus
            className="sg2-inline-textarea"
            rows={7}
            placeholder="Enter comment…"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
        <div className="sg2-inline-footer">
          <button className="sg2-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="sg2-primary-btn" onClick={onSave}>Save</button>
        </div>
      </div>
    </div>
  );
};

const SGActionBtn = ({ icon, label, onClick, variant = "default" }) => (
  <button
    className={`sg2-action-btn sg2-action-btn--${variant}`}
    onClick={onClick}
  >
    <span className="sg2-action-icon">{icon}</span>
    <span className="sg2-action-label">{label}</span>
  </button>
);

const SGQueueCard = ({ call, isActive, index, onClick }) => (
  <button
    className={`sg2-queue-card ${isActive ? "sg2-queue-card--active" : ""}`}
    onClick={onClick}
  >
    <div className="sg2-queue-num" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
      <span>#{index + 1}</span>
      <span style={{ fontSize: '8px', opacity: 0.6 }}>•</span>
      <span>{formatTime(call.receivedAt)}</span>
    </div>
    <div className="sg2-queue-name">
      {(call.leadName || call.phone || "Unknown").split(" ")[0]}
    </div>
    <div className="sg2-queue-status">{call.statusName || "Live Call"}</div>
  </button>
);

// ─── Inline Modals ─────────────────────────────
const SGHistoryModal = ({ open, lead, historyData, loading, onClose }) => {
  if (!open) return null;
  return (
    <div className="sg2-inline-overlay">
      <div className="sg2-inline-modal">
        <div className="sg2-inline-header">
          <span>Lead History</span>
          <button className="sg2-inline-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div
          className="sg2-inline-body"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          {loading ? (
            <div className="sg2-spinner mx-auto my-4" />
          ) : historyData.length === 0 ? (
            <p className="text-center text-gray-500">No history found.</p>
          ) : (
            historyData.map((item, idx) => (
              <div
                key={idx}
                style={{ padding: "10px", borderBottom: "1px solid #eee" }}
              >
                <strong style={{ fontSize: "13px" }}>
                  {item.changedByName || "System"}
                </strong>{" "}
                -{" "}
                <span style={{ fontSize: "11px", color: "#666" }}>
                  {new Date(item.createdAt).toLocaleString()}
                </span>
                <div style={{ fontSize: "12px", marginTop: "4px" }}>
                  {item.changeType}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const SGStatusModal = ({
  open,
  statuses,
  currentStatus,
  onConfirm,
  onClose,
}) => {
  const [selected, setSelected] = useState(currentStatus || "");
  const [reason, setReason] = useState("");
  useEffect(() => {
    setSelected(currentStatus || "");
    setReason("");
  }, [currentStatus, open]);
  if (!open) return null;
  return (
    <div className="sg2-inline-overlay">
      <div className="sg2-inline-modal">
        <div className="sg2-inline-header">
          <span>Change Status</span>
          <button className="sg2-inline-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="sg2-inline-body">
          <label className="sg2-inline-label">New status</label>
          <select
            className="sg2-inline-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">— select —</option>
            {statuses.map((s) => (
              <option key={s.name} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
          <label className="sg2-inline-label" style={{ marginTop: 12 }}>
            Reason
          </label>
          <textarea
            className="sg2-inline-textarea"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="sg2-inline-footer">
          <button className="sg2-ghost-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="sg2-primary-btn"
            disabled={!selected}
            onClick={() => onConfirm(selected, reason)}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Appointment wizard modal (identical to Sales God: with inline editing for
//     history & SEARCH, "See All Departments/Persons" toggle) ────────────────
const SG_APT_PAGE_SIZE = 6;
const SG_APT_STATUS_OPTIONS = ["SCHEDULED", "COMPLETED", "CANCELLED", "RESCHEDULED", "NO_SHOW"];

const SGAppointmentModal = ({ open, lead, companyId, stableUser, users, onClose, onScheduled }) => {
  const [phase, setPhase] = useState("loading");
  const [appointments, setAppointments] = useState([]);

  const [step, setStep] = useState(1);
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [deptPage, setDeptPage] = useState(0);
  const [selectedDept, setSelectedDept] = useState(null);

  const [persons, setPersons] = useState([]);
  const [allPersons, setAllPersons] = useState([]); 
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [personPage, setPersonPage] = useState(0);
  const [selectedPerson, setSelectedPerson] = useState(null);

  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("SCHEDULED");
  const [submitting, setSubmitting] = useState(false);

  // Search State
  const [searchTerm, setSearchTerm] = useState("");

  // "See All Departments/Persons" toggle
  const [showAllDeptsDropdown, setShowAllDeptsDropdown] = useState(false);

  useEffect(() => {
    if (!open || !lead?.leadId) return;
    setPhase("loading");
    setAppointments([]);
    setStep(1);
    setSelectedDept(null);
    setSelectedPerson(null);
    setDeptPage(0);
    setPersonPage(0);
    setPreferredDate("");
    setPreferredTime("");
    setNotes("");
    setStatus("SCHEDULED");
    setPersons([]);
    setSearchTerm(""); // Reset search on open
    setShowAllDeptsDropdown(false);

    const loadAppointments = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/appointments/lead/${lead.leadId}`, {
          headers: authHeader(),
        });
        const list = Array.isArray(res.data) ? res.data : [];
        setAppointments(list);
        setPhase(list.length > 0 ? "history" : "wizard");
      } catch (err) {
        console.error("Failed to fetch appointment history", err);
        toast.error("Failed to load appointment history.");
        setAppointments([]);
        setPhase("wizard");
      }
    };
    loadAppointments();
  }, [open, lead?.leadId]);

  // Reset pagination when search term changes
  useEffect(() => {
    setDeptPage(0);
    setPersonPage(0);
  }, [searchTerm]);

  useEffect(() => {
    if (!open) return;
    const loadDepartments = async () => {
      setLoadingDepartments(true);
      try {
        const res = await axios.get(`${API_URL}/api/appointments/departments/company/${companyId}`, {
          headers: authHeader(),
          params: { activeOnly: true },
        });
        setDepartments(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch departments", err);
        toast.error("Failed to load departments.");
        setDepartments([]);
      } finally {
        setLoadingDepartments(false);
      }
    };

    const loadPersons = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/appointments/persons/company/${companyId}`, {
          headers: authHeader(),
          params: { activeOnly: true },
        });
        setAllPersons(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch persons", err);
        setAllPersons([]);
      }
    };

    loadDepartments();
    loadPersons();
  }, [open, companyId]);

  useEffect(() => {
    if (!open || phase !== "wizard" || step !== 2 || !selectedDept) return;
    const deptId = selectedDept.departmentId ?? selectedDept.id ?? selectedDept._id;

    const loadPersons = async () => {
      setLoadingPersons(true);
      setPersonPage(0);
      try {
        const res = await axios.get(`${API_URL}/api/appointments/persons/company/${companyId}`, {
          headers: authHeader(),
          params: { activeOnly: true, departmentId: deptId },
        });
        setPersons(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch persons", err);
        toast.error("Failed to load persons.");
        setPersons([]);
      } finally {
        setLoadingPersons(false);
      }
    };
    loadPersons();
  }, [open, phase, step, selectedDept, companyId]);

  const handleInlineAptUpdate = async (apt, field, value) => {
    if (!value) return;

    if (field === 'preferredTime' && apt.preferredTime) {
      const newTime = value.length === 5 ? `${value}:00` : value;
      if (apt.preferredTime === newTime) return;
    } else if (apt[field] === value) {
      return;
    }

    let payload = { ...apt, companyId, leadId: lead.leadId, leadName: lead.name, leadPhone: lead.phone, leadEmail: lead.email };

    const creator = users.find((u) => String(u.id) === String(stableUser.id));
    const creatorName = creator ? `${creator.fname} ${creator.lname}` : "";
    payload.lastUpdatedById = stableUser.id;
    payload.lastUpdatedByName = creatorName;

    let successMessage = "Appointment updated";

    if (field === 'departmentName') {
      const dept = departments.find(d => d.name === value);
      if (dept) {
        payload.departmentId = dept.departmentId ?? dept.id ?? dept._id;
        payload.departmentName = dept.name;
        payload.departmentLocation = dept.location;
        successMessage = "Department updated successfully";
      }
    } else if (field === 'personName') {
      const person = allPersons.find(p => p.name === value);
      if (person) {
        payload.personId = person.personId ?? person.id ?? person._id;
        payload.personName = person.name;
        payload.personPosition = person.position;
        successMessage = "Person updated successfully";
      } else {
        payload.personName = value;
        successMessage = "Person updated successfully";
      }
    } else if (field === 'preferredDate') {
      payload.preferredDate = value;
      successMessage = "Date updated successfully";
    } else if (field === 'preferredTime') {
      payload.preferredTime = value.length === 5 ? `${value}:00` : value;
      successMessage = "Time updated successfully";
    } else if (field === 'status') {
      payload.status = value;
      successMessage = "Status updated successfully";
    } else if (field === 'notes') {
      payload.notes = value;
      successMessage = "Notes updated successfully";
    }

    try {
      const res = await axios.put(`${API_URL}/api/appointments/${apt.appointmentId}`, payload, { headers: authHeader() });
      toast.success(successMessage);
      setAppointments(prev => prev.map(a => a.appointmentId === apt.appointmentId ? { ...a, ...payload } : a));
      onScheduled?.();
    } catch (err) {
      console.error("Failed to update appointment inline", err);
      toast.error(err.response?.data?.message || "Failed to update appointment.");
    }
  };

  if (!open) return null;

  // Search Filter Logic for Departments
  const filteredDepartments = departments.filter(d => 
    (d.name || "").toLowerCase().includes(searchTerm.toLowerCase()) || 
    (d.location || "").toLowerCase().includes(searchTerm.toLowerCase())
  );
  const deptTotalPages = Math.max(1, Math.ceil(filteredDepartments.length / SG_APT_PAGE_SIZE));
  const pagedDepartments = filteredDepartments.slice(deptPage * SG_APT_PAGE_SIZE, (deptPage + 1) * SG_APT_PAGE_SIZE);

  // Search Filter Logic for Persons (for selected department)
  const filteredPersons = persons.filter(p => 
    (p.name || "").toLowerCase().includes(searchTerm.toLowerCase()) || 
    (p.position || "").toLowerCase().includes(searchTerm.toLowerCase())
  );
  const personTotalPages = Math.max(1, Math.ceil(filteredPersons.length / SG_APT_PAGE_SIZE));
  const pagedPersons = filteredPersons.slice(personPage * SG_APT_PAGE_SIZE, (personPage + 1) * SG_APT_PAGE_SIZE);

  const goToPersonsStep = () => { if (selectedDept) { setStep(2); setSearchTerm(""); setShowAllDeptsDropdown(false); } };
  const goToDetailsStep = () => { if (selectedPerson) setStep(3); };
  
  const goBack = () => {
    setSearchTerm("");
    setShowAllDeptsDropdown(false);
    if (step > 1) { setStep((s) => s - 1); return; }
    if (appointments.length > 0) setPhase("history");
  };

  const startNewAppointment = () => {
    setSelectedDept(null);
    setSelectedPerson(null);
    setDeptPage(0);
    setPersonPage(0);
    setPreferredDate("");
    setPreferredTime("");
    setNotes("");
    setStatus("SCHEDULED");
    setSearchTerm("");
    setShowAllDeptsDropdown(false);
    setStep(1);
    setPhase("wizard");
  };

  const handleSubmit = async () => {
    if (!lead || !selectedDept || !selectedPerson) return;
    if (!preferredDate || !preferredTime) {
      toast.error("Please pick a preferred date and time.");
      return;
    }

    const creator = users.find((u) => String(u.id) === String(stableUser.id));
    const creatorName = creator ? `${creator.fname} ${creator.lname}` : "";

    const deptId = selectedDept.departmentId ?? selectedDept.id ?? selectedDept._id;
    const personId = selectedPerson.personId ?? selectedPerson.id ?? selectedPerson._id;

    const payload = {
      companyId,
      leadId: lead.leadId,
      leadName: lead.name,
      leadPhone: lead.phone,
      leadEmail: lead.email,
      preferredDate,
      preferredTime: preferredTime.length === 5 ? `${preferredTime}:00` : preferredTime,
      departmentId: deptId,
      departmentName: selectedDept.name,
      departmentLocation: selectedDept.location,
      personId,
      personName: selectedPerson.name,
      personPosition: selectedPerson.position,
      status,
      notes,
      createdById: stableUser.id,
      createdByName: creatorName,
      lastUpdatedById: stableUser.id,
      lastUpdatedByName: creatorName,
    };

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/appointments`, payload, { headers: authHeader() });
      toast.success("Appointment scheduled!");
      onScheduled?.();
      onClose();
    } catch (err) {
      console.error("Failed to save appointment", err);
      toast.error(err.response?.data?.message || "Failed to save appointment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sg2-inline-overlay">
      <div className="sg2-inline-modal sg2-apt-modal-container">
        
        {/* MODIFIED HEADER WITH SEARCH */}
        <div className="sg2-inline-header" style={{ gap: '10px' }}>
          <span style={{ flexShrink: 0 }}>
            {phase === "loading" && "Loading Appointments"}
            {phase === "history" && "Appointment History"}
            {phase === "wizard" && step === 1 && "Select Department"}
            {phase === "wizard" && step === 2 && "Select Person"}
            {phase === "wizard" && step === 3 && "Schedule Appointment"}
          </span>
          
          {phase === "wizard" && (step === 1 || step === 2) && (
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', paddingRight: '10px', flexWrap: 'nowrap' }}>
              <div className="sg2-apt-search-wrap">
                <svg className="sg2-apt-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#673AB7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  type="text"
                  placeholder={`Search ${step === 1 ? 'department' : 'person'}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="sg2-inline-select sg2-apt-search-input"
                />
              </div>
              <button
                type="button"
                className={`sg2-apt-alldept-btn ${showAllDeptsDropdown ? "sg2-apt-alldept-btn--active" : ""}`}
                onClick={() => setShowAllDeptsDropdown((v) => !v)}
                title={step === 1 ? "Browse every department" : "Browse every person"}
              >
                {showAllDeptsDropdown ? "✕ Close List" : (step === 1 ? "See All Departments" : "See All Persons")}
              </button>
            </div>
          )}
          
          <button className="sg2-inline-close" onClick={onClose} style={{ flexShrink: 0 }}>✕</button>
        </div>

        {phase === "wizard" && (
          <div className="sg2-apt-steps">
            <span className={`sg2-apt-step ${step >= 1 ? "sg2-apt-step--active" : ""}`}>1. Department</span>
            <span className="sg2-apt-step-sep">→</span>
            <span className={`sg2-apt-step ${step >= 2 ? "sg2-apt-step--active" : ""}`}>2. Person</span>
            <span className="sg2-apt-step-sep">→</span>
            <span className={`sg2-apt-step ${step >= 3 ? "sg2-apt-step--active" : ""}`}>3. Details</span>
          </div>
        )}

        <div className="sg2-inline-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {phase === "loading" && (
            <div className="sg2-centered" style={{ padding: "40px 0" }}><div className="sg2-spinner" /></div>
          )}

          {phase === "history" && (
            <>
              <button type="button" className="sg2-apt-new-btn" onClick={startNewAppointment}>
                + Make New Appointment
              </button>
              <div className="sg2-apt-hist-list">
                {appointments.map((apt) => {
                  const deptPersons = allPersons.filter(p => {
                    const pDeptId = p.departmentId?._id ?? p.departmentId;
                    return pDeptId === apt.departmentId;
                  });

                  return (
                    <div key={apt.appointmentId} className="sg2-apt-hist-card">
                      <div className="sg2-apt-hist-top" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#0C4550' }}>Department:</span>
                            <SGEditableField
                              className="sg2-apt-hist-dept"
                              value={apt.departmentName}
                              onSave={(v) => handleInlineAptUpdate(apt, 'departmentName', v)}
                              label="Department"
                              options={departments}
                            />
                          </div>

                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#0C4550' }}>Status:</span>
                            <span className={`sg2-apt-status-badge sg2-apt-status-badge--${(apt.status || "").toLowerCase()}`}>
                              <SGEditableField
                                value={apt.status}
                                onSave={(v) => handleInlineAptUpdate(apt, 'status', v)}
                                label="Status"
                                options={SG_APT_STATUS_OPTIONS.map(s => ({ name: s }))}
                              />
                            </span>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#0C4550' }}>Person:</span>
                          <SGEditableField
                            className="sg2-apt-hist-person"
                            value={apt.personName}
                            onSave={(v) => handleInlineAptUpdate(apt, 'personName', v)}
                            label="Person"
                            options={deptPersons.length > 0 ? deptPersons : allPersons}
                          />
                          {apt.personPosition && <span style={{ fontSize: '12px', color: '#6b7280' }}>• {apt.personPosition}</span>}
                        </div>
                      </div>

                      <div className="sg2-apt-hist-meta" style={{ display: 'flex', gap: '20px', marginTop: '12px', fontSize: '12.5px', color: '#4b5563' }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600 }}>📅 Date:</span>
                          <SGEditableField
                            type="date"
                            value={apt.preferredDate}
                            onSave={(v) => handleInlineAptUpdate(apt, 'preferredDate', v)}
                            label="Date"
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600 }}>🕒 Time:</span>
                          <SGEditableField
                            type="time"
                            value={apt.preferredTime ? apt.preferredTime.substring(0, 5) : ""}
                            onSave={(v) => handleInlineAptUpdate(apt, 'preferredTime', v)}
                            label="Time"
                          />
                        </div>
                      </div>

                      <div className="sg2-apt-hist-notes" style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', background: 'transparent', border: 'none', padding: 0, marginTop: '14px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: '#0C4550', flexShrink: 0 }}>Notes:</span>
                        <SGEditableField
                          value={apt.notes}
                          onSave={(v) => handleInlineAptUpdate(apt, 'notes', v)}
                          label="Notes"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {phase === "wizard" && step === 1 && showAllDeptsDropdown && (
            filteredDepartments.length === 0 ? (
              <p className="sg2-history-empty">No departments match your search.</p>
            ) : (
              <div className="sg2-apt-alldept-list">
                {filteredDepartments.map((dept) => {
                  const deptId = dept.departmentId ?? dept.id ?? dept._id;
                  const isSelected = selectedDept && (selectedDept.departmentId ?? selectedDept.id ?? selectedDept._id) === deptId;
                  return (
                    <div key={deptId} className="sg2-apt-alldept-group" style={{ padding: "12px 14px" }}>
                      <button
                        type="button"
                        className="sg2-apt-alldept-dept-name"
                        onClick={() => { setSelectedDept(dept); }}
                        title="Select this department"
                        style={{ borderBottom: "none", margin: 0, padding: 0 }}
                      >
                        {dept.name}
                        {dept.location && <span className="sg2-apt-alldept-dept-loc"> · {dept.location}</span>}
                        {isSelected && <span className="sg2-apt-check" style={{ position: "relative", display: "inline-flex", top: 0, right: 0, marginLeft: "10px" }}>✓</span>}
                      </button>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {phase === "wizard" && step === 1 && !showAllDeptsDropdown && (
            loadingDepartments ? (
              <div className="sg2-centered" style={{ padding: "30px 0" }}><div className="sg2-spinner" /></div>
            ) : filteredDepartments.length === 0 ? (
              <p className="sg2-history-empty">No departments found.</p>
            ) : (
              <>
                <div className="sg2-apt-grid">
                  {pagedDepartments.map((d) => {
                    const id = d.departmentId ?? d.id ?? d._id;
                    const isSelected = selectedDept && (selectedDept.departmentId ?? selectedDept.id ?? selectedDept._id) === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`sg2-apt-card ${isSelected ? "sg2-apt-card--selected" : ""}`}
                        onClick={() => setSelectedDept(d)}
                      >
                        {isSelected && <span className="sg2-apt-check">✓</span>}
                        <div className="sg2-apt-card-title">{d.name}</div>
                        <div className="sg2-apt-card-sub">{d.location || "No location set"}</div>
                      </button>
                    );
                  })}
                </div>
                {deptTotalPages > 1 && (
                  <div className="sg2-pagination sg2-apt-pagination">
                    <span>Page {deptPage + 1} of {deptTotalPages}</span>
                    <div className="sg2-apt-pagination-btns">
                      <button disabled={deptPage === 0} onClick={() => setDeptPage((p) => p - 1)}>← Prev</button>
                      <button disabled={deptPage === deptTotalPages - 1} onClick={() => setDeptPage((p) => p + 1)}>Next →</button>
                    </div>
                  </div>
                )}
              </>
            )
          )}

          {phase === "wizard" && step === 2 && showAllDeptsDropdown && (
            filteredPersons.length === 0 ? (
              <p className="sg2-history-empty">No persons match your search.</p>
            ) : (
              <div className="sg2-apt-alldept-list">
                {filteredPersons.map((p) => {
                  const pId = p.personId ?? p.id ?? p._id;
                  const isSelected = selectedPerson && (selectedPerson.personId ?? selectedPerson.id ?? selectedPerson._id) === pId;
                  return (
                    <div key={pId} className="sg2-apt-alldept-group" style={{ padding: "12px 14px" }}>
                      <button
                        type="button"
                        className="sg2-apt-alldept-dept-name"
                        onClick={() => {
                          setSelectedPerson(p);
                          setShowAllDeptsDropdown(false);
                          setSearchTerm("");
                          setStep(3);
                        }}
                        title="Select this person"
                        style={{ borderBottom: "none", margin: 0, padding: 0 }}
                      >
                        {p.name}
                        {p.position && <span className="sg2-apt-alldept-dept-loc"> · {p.position}</span>}
                        {isSelected && <span className="sg2-apt-check" style={{ position: "relative", display: "inline-flex", top: 0, right: 0, marginLeft: "10px" }}>✓</span>}
                      </button>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {phase === "wizard" && step === 2 && !showAllDeptsDropdown && (
            loadingPersons ? (
              <div className="sg2-centered" style={{ padding: "30px 0" }}><div className="sg2-spinner" /></div>
            ) : filteredPersons.length === 0 ? (
              <p className="sg2-history-empty">No persons found in this department.</p>
            ) : (
              <>
                <div className="sg2-apt-grid">
                  {pagedPersons.map((p) => {
                    const id = p.personId ?? p.id ?? p._id;
                    const isSelected = selectedPerson && (selectedPerson.personId ?? selectedPerson.id ?? selectedPerson._id) === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`sg2-apt-card ${isSelected ? "sg2-apt-card--selected" : ""}`}
                        onClick={() => setSelectedPerson(p)}
                      >
                        {isSelected && <span className="sg2-apt-check">✓</span>}
                        <div className="sg2-apt-card-title">{p.name}</div>
                        <div className="sg2-apt-card-sub">{p.position || "No position set"}</div>
                      </button>
                    );
                  })}
                </div>
                {personTotalPages > 1 && (
                  <div className="sg2-pagination sg2-apt-pagination">
                    <span>Page {personPage + 1} of {personTotalPages}</span>
                    <div className="sg2-apt-pagination-btns">
                      <button disabled={personPage === 0} onClick={() => setPersonPage((p) => p - 1)}>← Prev</button>
                      <button disabled={personPage === personTotalPages - 1} onClick={() => setPersonPage((p) => p + 1)}>Next →</button>
                    </div>
                  </div>
                )}
              </>
            )
          )}

          {phase === "wizard" && step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div className="sg2-apt-summary">
                <div><span className="sg2-apt-summary-label">Department:</span> {selectedDept?.name}</div>
                <div><span className="sg2-apt-summary-label">Person:</span> {selectedPerson?.name}{selectedPerson?.position ? ` (${selectedPerson.position})` : ""}</div>
                <div><span className="sg2-apt-summary-label">Lead:</span> {lead?.name}</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label className="sg2-inline-label">Preferred date</label>
                  <input className="sg2-inline-select" type="date" value={preferredDate} min={new Date().toISOString().split("T")[0]} onChange={(e) => setPreferredDate(e.target.value)} />
                </div>
                <div>
                  <label className="sg2-inline-label">Preferred time</label>
                  <input className="sg2-inline-select" type="time" value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="sg2-inline-label">Notes</label>
                <textarea className="sg2-inline-textarea" rows={3} placeholder="Enter notes…" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
          )}
        </div>

        <div className="sg2-inline-footer">
          {phase === "history" && (
            <button className="sg2-ghost-btn" onClick={onClose}>Close</button>
          )}
          {phase === "wizard" && (
            <>
              {(step > 1 || appointments.length > 0) && (
                <button className="sg2-ghost-btn" onClick={goBack} disabled={submitting}>Back</button>
              )}
              <button className="sg2-ghost-btn" onClick={onClose} disabled={submitting}>Cancel</button>
              {step === 1 && <button className="sg2-primary-btn" disabled={!selectedDept} onClick={goToPersonsStep}>Next</button>}
              {step === 2 && <button className="sg2-primary-btn" disabled={!selectedPerson} onClick={goToDetailsStep}>Next</button>}
              {step === 3 && (
                <button className="sg2-primary-btn" disabled={submitting} onClick={handleSubmit}>
                  {submitting ? "Scheduling…" : "Schedule"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────
const IncomingMode = () => {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    loading: authLoading,
  } = useContext(AuthContext);
  const stableUser = useMemo(
    () => ({
      id: user?.id,
      accountType: user?.accountType,
      companyId: user?.companyId,
    }),
    [user],
  );
  const companyId = useMemo(
    () =>
      isOwnerLevel(stableUser.accountType)
        ? stableUser.id
        : stableUser.companyId,
    [stableUser],
  );
  // Straight off the JWT rather than AuthContext, per the "today's leads" endpoint
  const executiveId = useMemo(
    () => getUserIdFromToken() || stableUser.id,
    [stableUser.id],
  );

  const [connectionState, setConnectionState] = useState("connecting");
  const [calls, setCalls] = useState([]); // List of incoming call payloads
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeLeadDetails, setActiveLeadDetails] = useState(null); // Full lead data from DB
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Pagination states for the queue
  const leadsPerPage = 14;
  const [leadPage, setLeadPage] = useState(0);

  const [statuses, setStatuses] = useState([]);
  const [labels, setLabels] = useState([]);
  const [users, setUsers] = useState([]);

  // Modals
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);

  // New Comment Modal states
  const [showComment, setShowComment] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");

  const anyModalOpen = showStatus || showHistory || showComment || showAppointment;

  const clientRef = useRef(null);

  // Keypad Navigation Setup
  useEffect(() => {
    const handleKey = (e) => {
      if (anyModalOpen) return;

      const tag = e.target.tagName;
      const isInput = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
      if (isInput) return;

      if (e.key === "ArrowRight") {
        setActiveIndex((i) => Math.min(i + 1, calls.length - 1));
      }
      if (e.key === "ArrowLeft") {
        setActiveIndex((i) => Math.max(i - 1, 0));
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [calls.length, anyModalOpen]);

  // Fetch Support Data
  useEffect(() => {
    if (!companyId) return;
    axios
      .get(`${API_URL}/api/leadStatus/company/${companyId}`, {
        headers: authHeader(),
      })
      .then((res) => setStatuses(res.data || []));
    axios
      .get(`${API_URL}/api/projectName/company/${companyId}`, {
        headers: authHeader(),
      })
      .then((res) => setLabels(res.data || []));
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !stableUser.id || !stableUser.accountType) return;
    const usersUrl = isOwnerLevel(stableUser.accountType)
      ? `${API_URL}/api/companyuser/getByUserId?userId=${stableUser.id}&accountType=${stableUser.accountType}`
      : `${API_URL}/api/companyuser/getSameAndSubordinateUsers?userId=${stableUser.id}&accountType=${stableUser.accountType}`;

    axios
      .get(usersUrl, { headers: authHeader() })
      .then((res) => setUsers(res.data || []))
      .catch((err) => console.warn("Failed to load users", err));
  }, [companyId, stableUser.id, stableUser.accountType]);

  useEffect(() => {
    setLeadPage(Math.floor(activeIndex / leadsPerPage));
  }, [activeIndex]);

  useEffect(() => {
    const activeCall = calls[activeIndex];
    if (!activeCall) {
      setActiveLeadDetails(null);
      return;
    }

    setActiveLeadDetails({
      leadId: activeCall.leadId,
      name: activeCall.leadName,
      phone: activeCall.phone,
      statusName: activeCall.statusName,
      projectName: activeCall.projectName,
      assignedToName: activeCall.assignedToName,
    });

    if (!activeCall.leadId) return;

    let cancelled = false;
    let prevRecordingCount = 0;

    const fetchLeadDetails = async ({ silent } = {}) => {
      if (!silent) setLoadingDetails(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/leads/${activeCall.leadId}?requesterId=${stableUser.id}`,
          { headers: authHeader() },
        );

        const fullLead = res.data?.lead || res.data?.data || res.data;

        if (!cancelled && fullLead && fullLead.leadId) {
          setActiveLeadDetails(fullLead);
          prevRecordingCount = fullLead.callRecordings?.length || 0;
        }
        return fullLead;
      } catch (err) {
        console.warn(
          "Failed to fetch full lead details, using WebSocket data fallback",
          err,
        );
        return null;
      } finally {
        if (!silent) setLoadingDetails(false);
      }
    };

    fetchLeadDetails();

    let attempts = 0;
    const maxAttempts = 20; 
    const pollId = setInterval(async () => {
      attempts += 1;
      if (cancelled || attempts > maxAttempts) {
        clearInterval(pollId);
        return;
      }
      const fullLead = await fetchLeadDetails({ silent: true });
      const newCount = fullLead?.callRecordings?.length || 0;
      if (newCount > 0 && newCount >= prevRecordingCount) {
        clearInterval(pollId);
      }
    }, 6000);

    return () => {
      cancelled = true;
      clearInterval(pollId);
    };
  }, [activeIndex, calls, stableUser.id]);

  useEffect(() => {
    if (!executiveId) return;
    let cancelled = false;

    axios
      .get(`${API_URL}/api/leads/calls-today/${executiveId}?page=0&size=50`, {
        headers: authHeader(),
      })
      .then((res) => {
        if (cancelled) return;
        const todaysCalls = (res.data || []).map(mapCallActivityToCallPayload);
        setCalls((prev) => {
          const existingIds = new Set(prev.map((c) => c.leadId));
          const merged = [
            ...prev,
            ...todaysCalls.filter((c) => !existingIds.has(c.leadId)),
          ];
          return merged.slice(0, 100);
        });
      })
      .catch((err) => {
        console.warn("Failed to load today's leads", err);
      });

    return () => {
      cancelled = true;
    };
  }, [executiveId]);

  const handleIncomingCall = useCallback((payload) => {
    if (payload?.event && payload.event !== "CALL_ANSWERED") return;
    setCalls((prev) => {
      const withoutDuplicate = prev.filter((c) => c.leadId !== payload.leadId);
      const newCalls = [
        { ...payload, receivedAt: Date.now() },
        ...withoutDuplicate,
      ].slice(0, 100);
      return newCalls;
    });
    setActiveIndex(0);
    toast.info(
      `📞 Connected: ${payload.leadName || payload.phone || "a lead"}`,
      { position: "top-right" },
    );
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !companyId || !stableUser.id) return;
    setConnectionState("connecting");

    const client = new Client({
      webSocketFactory: () =>
        new SockJS(
          `${API_URL}/ws/live-calls?executiveId=${stableUser.id}&companyId=${companyId}`,
        ),
      reconnectDelay: 5000,
      onConnect: () => {
        setConnectionState("live");
        client.subscribe("/user/queue/live-call", (message) => {
          try {
            handleIncomingCall(JSON.parse(message.body));
          } catch (err) {
            console.error("Failed to parse live-call payload:", err);
          }
        });
      },
      onWebSocketClose: () => setConnectionState("disconnected"),
      onStompError: () => setConnectionState("disconnected"),
    });

    clientRef.current = client;
    client.activate();
    return () => client.deactivate();
  }, [
    authLoading,
    isAuthenticated,
    companyId,
    stableUser.id,
    handleIncomingCall,
  ]);

  const handleInlineEdit = async (field, value) => {
    if (!activeLeadDetails) return;
    try {
      await axios.put(
        `${API_URL}/api/leads/update/${activeLeadDetails.leadId}/changedBy/${stableUser.id}`,
        { [field]: value },
        { headers: authHeader() },
      );
      setActiveLeadDetails((prev) => ({ ...prev, [field]: value }));
      toast.success("Updated successfully");
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const handleStatusChange = async (statusName, reason) => {
    if (!activeLeadDetails) return;
    const statusObj = statuses.find((s) => s.name === statusName);
    try {
      await axios.put(
        `${API_URL}/api/leads/update/${activeLeadDetails.leadId}/changedBy/${stableUser.id}`,
        { statusName, statusColor: statusObj?.color, reason },
        { headers: authHeader() },
      );
      setActiveLeadDetails((prev) => ({
        ...prev,
        statusName,
        statusColor: statusObj?.color,
      }));
      setShowStatus(false);
      toast.success("Status updated");
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const openHistory = async () => {
    if (!activeLeadDetails) return;
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/leadHistory/${activeLeadDetails.leadId}`,
        { headers: authHeader() },
      );
      setHistoryData(res.data?.content || []);
    } catch (err) {
      toast.error("Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openCommentModal = () => {
    setCommentDraft(activeLeadDetails?.comment || "");
    setShowComment(true);
  };

  const handleCommentSave = async () => {
    const raw = commentDraft || "";
    const trimmed = raw.trim();

    if (raw.length > 0 && trimmed === "") {
      toast.error("Comment cannot contain only spaces.");
      return;
    }

    const original = (activeLeadDetails?.comment || "").trim();
    if (trimmed !== original) {
      await handleInlineEdit("comment", trimmed);
    }
    setShowComment(false);
  };

  const statusMeta = {
    connecting: { label: "Connecting…", color: "#f59e0b" },
    live: { label: "Live Listening", color: "#22c55e" },
    disconnected: { label: "Reconnecting…", color: "#ef4444" },
  }[connectionState];

  const totalLeadPages = Math.ceil(calls.length / leadsPerPage);
  const paginatedCalls = calls.slice(leadPage * leadsPerPage, (leadPage + 1) * leadsPerPage);

  const handleNextLeadPage = () => {
    const nextPage = leadPage + 1;
    if (nextPage < totalLeadPages) {
      setActiveIndex(nextPage * leadsPerPage);
    }
  };

  const handlePrevLeadPage = () => {
    const prevPage = leadPage - 1;
    if (prevPage >= 0) {
      setActiveIndex(prevPage * leadsPerPage);
    }
  };

  return (
    <>
      <div className="sg2-overlay">
        {/* HEADER */}
        <div className="sg2-header" style={{ minHeight: "50px" }}>
          <div className="sg2-header-title">
            <span className="sg2-header-brand">Incoming Mode</span>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                fontWeight: 600,
                background: "#f1f5f9",
                padding: "4px 10px",
                borderRadius: 999,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: statusMeta.color,
                  boxShadow: `0 0 0 3px color-mix(in srgb, ${statusMeta.color} 25%, transparent)`,
                }}
              />
              {statusMeta.label}
            </span>
          </div>
          <div className="sg2-header-right">
            <button onClick={() => navigate(-1)} className="sg2-close-btn">
              ✕
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="sg2-body">
          {calls.length === 0 ? (
            <div className="sg2-centered" style={{ flexDirection: "column" }}>
              <div style={{ fontSize: "40px", marginBottom: "12px" }}>🎧</div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#374151",
                }}
              >
                Ready for Calls
              </div>
              <p style={{ color: "#6b7280", marginTop: "4px" }}>
                When a call connects, the lead's complete CRM profile will pop
                up here instantly.
              </p>
            </div>
          ) : (
            <>
              <div className="sg2-workspace">
                {/* LEFT: Lead Detail */}
                <div className="sg2-detail-panel">
                  {loadingDetails ? (
                    <div className="sg2-centered">
                      <div className="sg2-spinner sg2-spinner--lg" />
                    </div>
                  ) : activeLeadDetails ? (
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeLeadDetails.leadId}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.18 }}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 20,
                          height: "100%",
                        }}
                      >
                        <div className="sg2-lead-identity-top">
                          <SGAvatar
                            name={activeLeadDetails.name || "?"}
                            size={56}
                          />
                          <div
                            className="sg2-lead-top-details"
                            style={{ width: "100%" }}
                          >
                            <SGEditableField
                              className="sg2-lead-name"
                              value={activeLeadDetails.name}
                              onSave={(v) => handleInlineEdit("name", v)}
                              label="Name"
                            />

                            <div
                              className="sg2-lead-top-row-info"
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                                flexWrap: "wrap",
                                gap: "10px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span className="sg2-contact-badge">
                                  <span className="sg2-badge-icon">📞</span>
                                  {isPhoneMasked(activeLeadDetails.phone) ? (
                                    <span className="sg2-header-inline">
                                      {activeLeadDetails.phone}
                                    </span>
                                  ) : (
                                    <SGEditableField
                                      className="sg2-header-inline"
                                      value={activeLeadDetails.phone}
                                      onSave={(v) =>
                                        handleInlineEdit("phone", v)
                                      }
                                      label="Phone"
                                    />
                                  )}
                                </span>
                                <span className="sg2-contact-badge">
                                  <span className="sg2-badge-icon">✉️</span>
                                  <SGEditableField
                                    className="sg2-header-inline"
                                    value={activeLeadDetails.email}
                                    onSave={(v) => handleInlineEdit("email", v)}
                                    label="Email"
                                  />
                                </span>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginLeft: "auto",
                                }}
                              >
                                <button
                                  className="sg2-history-trigger-btn"
                                  onClick={openHistory}
                                  title="Lead History"
                                  style={{ padding: "4px 10px", marginLeft: 0 }}
                                >
                                  <span className="sg2-badge-icon">🕘</span>{" "}
                                  <span>History</span>
                                </button>
                                <span
                                  className="sg2-status-pill"
                                  style={{
                                    background:
                                      activeLeadDetails.statusColor ||
                                      "#0C4550",
                                    marginLeft: 0,
                                    padding: "4px 12px",
                                  }}
                                >
                                  {activeLeadDetails.statusName}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="sg2-info-grid">
                          <SGInfoRow
                            icon="📋"
                            label="Project"
                            value={activeLeadDetails.projectName}
                            field="projectName"
                            onSave={handleInlineEdit}
                            options={labels}
                          />
                          <SGInfoRow
                            icon="🔗"
                            label="Source"
                            value={activeLeadDetails.sourceName}
                            field="sourceName"
                            onSave={handleInlineEdit}
                          />
                          <SGInfoRow
                            icon="👤"
                            label="Assigned to"
                            value={activeLeadDetails.assignedToName}
                          />
                          <SGInfoRow
                            icon="👤"
                            label="Created by"
                            value={activeLeadDetails.createdByName}
                          />
                          <SGInfoRow
                            icon="📍"
                            label="City"
                            value={activeLeadDetails.city}
                            field="city"
                            onSave={handleInlineEdit}
                          />
                          <SGInfoRow
                            icon="📍"
                            label="State"
                            value={activeLeadDetails.state}
                            field="state"
                            onSave={handleInlineEdit}
                          />
                          <SGInfoRow
                            icon="🏠"
                            label="Address"
                            value={activeLeadDetails.address}
                            field="address"
                            onSave={handleInlineEdit}
                          />
                          
                          <SGCommentRow 
                            value={activeLeadDetails.comment} 
                            onClick={openCommentModal} 
                          />
                        </div>

                        <div className="sg2-section" style={{ marginTop: 4 }}>
                          <div className="sg2-section-title">
                            Call Recordings
                            {activeLeadDetails.callRecordings?.length > 0 &&
                              ` (${activeLeadDetails.callRecordings.length})`}
                          </div>

                          {activeLeadDetails.lastCallAt && (
                            <div
                              style={{
                                fontSize: 12,
                                color: "#6b7280",
                                marginBottom: 10,
                              }}
                            >
                              Last call: {formatTime(activeLeadDetails.lastCallAt)}{" "}
                              on{" "}
                              {new Date(
                                activeLeadDetails.lastCallAt
                              ).toLocaleDateString()}
                            </div>
                          )}

                          {activeLeadDetails.callRecordings &&
                          activeLeadDetails.callRecordings.length > 0 ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                              }}
                            >
                              {activeLeadDetails.callRecordings
                                .slice()
                                .reverse()
                                .map((rec) => (
                                  <div
                                    key={rec.callSessionId}
                                    style={{
                                      background: "#f8fafc",
                                      border: "1px solid #e2e8f0",
                                      borderRadius: 12,
                                      padding: "10px 14px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        flexWrap: "wrap",
                                        gap: 8,
                                        marginBottom: 8,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                          fontSize: 13,
                                          color: "#111827",
                                          fontWeight: 600,
                                        }}
                                      >
                                        <span>
                                          {new Date(
                                            rec.startTime
                                          ).toLocaleString([], {
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            padding: "2px 8px",
                                            borderRadius: 20,
                                            color:
                                              rec.status === "Answer"
                                                ? "#0a7d4f"
                                                : "#b91c1c",
                                            background:
                                              rec.status === "Answer"
                                                ? "#e6f7ef"
                                                : "#fdecec",
                                          }}
                                        >
                                          {rec.status}
                                        </span>
                                      </div>
                                      <span
                                        style={{ fontSize: 12, color: "#6b7280" }}
                                      >
                                        {parseInt(rec.callDuration, 10) || 0}s
                                      </span>
                                    </div>

                                    {rec.status === "Answer" &&
                                    parseInt(rec.callDuration, 10) > 0 ? (
                                      <audio
                                        controls
                                        preload="none"
                                        src={rec.resourceURL}
                                        style={{ width: "100%", height: 32 }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          fontSize: 12,
                                          color: "#9ca3af",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        No audio available
                                        {rec.hangupReason
                                          ? ` — ${rec.hangupReason}`
                                          : ""}
                                      </div>
                                    )}
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <div
                              style={{
                                fontSize: 13,
                                color: "#9ca3af",
                                fontStyle: "italic",
                                background: "#f8fafc",
                                border: "1px dashed #e2e8f0",
                                borderRadius: 12,
                                padding: "14px",
                                textAlign: "center",
                              }}
                            >
                              No call recordings yet for this lead.
                            </div>
                          )}
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  ) : (
                    <div className="sg2-centered">
                      Lead details not available.
                    </div>
                  )}
                </div>

                {/* RIGHT: Fast Actions */}
                <div className="sg2-actions-panel">
                  <div className="sg2-section">
                    <div className="sg2-section-title">Live Call Actions</div>
                    <div
                      className="sg2-actions-grid"
                      style={{ gridTemplateColumns: "1fr" }}
                    >
                      <SGActionBtn
                        icon="🔄"
                        label="Change Status"
                        onClick={() => setShowStatus(true)}
                      />
                      <SGActionBtn
                        icon="🕘"
                        label="View History"
                        onClick={openHistory}
                      />
                      <SGActionBtn
                        icon="🗓️"
                        label="Appointment"
                        onClick={() => setShowAppointment(true)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* BOTTOM: Call Queue / History */}
              <div className="sg2-queue-wrap" style={{ marginTop: "10px" }}>
                <button 
                  className="sg2-queue-nav" 
                  disabled={leadPage === 0} 
                  onClick={handlePrevLeadPage}
                >
                  ←
                </button>
                <div className="sg2-queue-track">
                  {paginatedCalls.map((call, i) => {
                    const actualIndex = leadPage * leadsPerPage + i;
                    return (
                      <SGQueueCard
                        key={`${call.leadId}-${call.receivedAt}`}
                        call={call}
                        index={actualIndex}
                        isActive={actualIndex === activeIndex}
                        onClick={() => setActiveIndex(actualIndex)}
                      />
                    );
                  })}
                </div>
                <button
                  className="sg2-queue-nav"
                  disabled={leadPage >= totalLeadPages - 1}
                  onClick={handleNextLeadPage}
                >
                  →
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <SGStatusModal
        open={showStatus}
        statuses={statuses}
        currentStatus={activeLeadDetails?.statusName}
        onConfirm={handleStatusChange}
        onClose={() => setShowStatus(false)}
      />
      <SGHistoryModal
        open={showHistory}
        lead={activeLeadDetails}
        historyData={historyData}
        loading={historyLoading}
        onClose={() => setShowHistory(false)}
      />

      <SGAppointmentModal
        open={showAppointment}
        lead={activeLeadDetails}
        companyId={companyId}
        stableUser={stableUser}
        users={users}
        onClose={() => setShowAppointment(false)}
        onScheduled={() => {}}
      />
      
      <SGCommentModal 
        open={showComment} 
        value={commentDraft} 
        onChange={setCommentDraft} 
        onSave={handleCommentSave} 
        onClose={() => setShowComment(false)} 
      />

      <style>{SG_CSS}</style>
    </>
  );
};

const SG_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

  /* Loading Spinner */
  .sg2-spinner { width: 24px; height: 24px; border: 2.5px solid #e5e9f0; border-top-color: #0C4550; border-radius: 50%; animation: sg-spin .7s linear infinite; }
  .sg2-spinner--lg { width: 40px; height: 40px; border-width: 3.5px; }
  @keyframes sg-spin { to { transform: rotate(360deg); } }

  .sg2-overlay { position: fixed; inset: 0; z-index: 9998; background: #f0f4f8; display: flex; flex-direction: column; font-family: 'Plus Jakarta Sans', sans-serif; overflow: hidden; }
  .sg2-header { background: #fff; border-bottom: 1px solid #e5e9f0; padding: 10px 24px; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 1px 4px rgba(0,0,0,.05); flex-shrink: 0; }
  .sg2-header-title { display: flex; align-items: baseline; gap: 10px; }
  .sg2-header-brand { font-size: 20px; font-weight: 700; color: #0C4550; letter-spacing: -0.4px; }
  .sg2-header-right { display: flex; align-items: center; gap: 14px; }
  .sg2-close-btn { background: none; border: none; font-size: 20px; color: black; cursor: pointer; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; }
  
  .sg2-body { flex: 1; display: flex; flex-direction: column; overflow: hidden; padding: 20px 24px 16px; gap: 16px; }
  .sg2-centered { flex: 1; display: flex; align-items: center; justify-content: center; }

  /* Workspace Layout */
  .sg2-workspace { display: grid; grid-template-columns: 1fr 320px; gap: 16px; flex: 1; min-height: 0; overflow: hidden; }
  @media (max-width: 900px) { .sg2-workspace { grid-template-columns: 1fr; overflow-y: auto; } }

  /* Detail Panel */
  .sg2-detail-panel { background: #fff; border: 1px solid #e5e9f0; border-radius: 16px; padding: 24px; display: flex; flex-direction: column; gap: 20px; overflow-y: auto; box-shadow: 0 1px 4px rgba(0,0,0,.04); }
  .sg2-lead-identity-top { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; padding-bottom: 20px; border-bottom: 1px solid #f1f5f9; }
  .sg2-lead-top-details { display: flex; flex-direction: column; gap: 8px; flex: 1; min-width: 250px; }
  .sg2-contact-badge { display: inline-flex; align-items: center; gap: 6px; background: #f8fafc; border: 1px solid #e2e8f0; padding: 5px 12px; border-radius: 20px; }
  .sg2-badge-icon { font-size: 14px; }
  .sg2-status-pill { color: #fff; font-size: 12px; font-weight: 600; padding: 6px 16px; border-radius: 20px; white-space: nowrap; }
  .sg2-history-trigger-btn { display: inline-flex; align-items: center; gap: 6px; background: #f3e8ff; border: 1px solid #e4d3fb; color: #673AB7; font-size: 12px; font-weight: 700; padding: 6px 14px; border-radius: 20px; cursor: pointer; font-family: inherit; transition: all .15s; }
  .sg2-history-trigger-btn:hover { background: #ecdcff; border-color: #d9bffb; transform: translateY(-1px); }

  /* Info Grid */
  .sg2-info-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 12px; }
  .sg2-info-row { display: flex; align-items: center; gap: 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px 16px; }
  .sg2-info-icon { font-size: 18px; flex-shrink: 0; background: #fff; width: 36px; height: 36px; border-radius: 8px; border: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: center; }
  .sg2-info-content { display: flex; flex-direction: column; min-width: 0; flex: 1; }
  .sg2-info-label { font-size: 11px; color: #4b5563; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 2px; }
  .sg2-info-value { font-size: 14px; color: #111827; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  /* Comment row CSS added from SalesGod */
  .sg2-info-row--comment { cursor: pointer; grid-column: 1 / -1; }
  .sg2-comment-value { white-space: normal !important; overflow: visible !important; text-overflow: unset !important; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.4; }

  /* Actions Panel */
  .sg2-actions-panel { background: #fff; border: 1px solid #e5e9f0; border-radius: 16px; padding: 20px; display: flex; flex-direction: column; gap: 20px; overflow-y: auto; box-shadow: 0 1px 4px rgba(0,0,0,.04); }
  .sg2-section-title { font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 10px; }
  .sg2-actions-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .sg2-action-btn { display: flex; flex-direction: column; align-items: center; gap: 5px; padding: 12px 8px; border-radius: 10px; border: 1px solid #e5e9f0; background: #f8fafc; cursor: pointer; font-family: inherit; transition: background .12s; }
  .sg2-action-btn:hover { background: #eef2f9; border-color: #c7d2e8; }
  .sg2-action-icon { font-size: 18px; }
  .sg2-action-label { font-size: 11px; font-weight: 600; color: #374151; }

  /* Queue */
  .sg2-queue-wrap { display: flex; align-items: center; gap: 10px; background: #fff; border: 1px solid #e5e9f0; border-radius: 14px; padding: 10px 14px; flex-shrink: 0; box-shadow: 0 1px 3px rgba(0,0,0,.04); }
  .sg2-queue-track { display: grid; grid-template-columns: repeat(14, minmax(0, 1fr)); gap: 8px; flex: 1; overflow: hidden; padding: 2px 0; }
  .sg2-queue-card { display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 8px 14px; border-radius: 10px; border: 1.5px solid #e5e9f0; background: #f8fafc; cursor: pointer; min-width: 0; width: 100%; flex-shrink: 0; transition: all .15s;}
  .sg2-queue-card:hover { background: #eef2f9; border-color: #c7d2e8; }
  .sg2-queue-card--active { background: #0C4550; border-color: #0C4550; box-shadow: 0 3px 10px rgba(12,69,80,.2); }
  .sg2-queue-num { font-size: 10px; color: #9ca3af; font-weight: 600; }
  .sg2-queue-card--active .sg2-queue-num { color: rgba(255,255,255,0.6); }
  .sg2-queue-name { font-size: 13px; font-weight: 600; color: #1a1f36; white-space: nowrap; max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
  .sg2-queue-card--active .sg2-queue-name { color: #fff; }
  .sg2-queue-status { font-size: 10px; color: #6b7280; white-space: nowrap; max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
  .sg2-queue-card--active .sg2-queue-status { color: rgba(255,255,255,0.7); }
  .sg2-queue-nav { background: none; border: 1px solid #e5e9f0; border-radius: 8px; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; font-size: 16px; cursor: pointer; color: #4b5563; transition: background .1s; flex-shrink: 0; }
  .sg2-queue-nav:hover:not(:disabled) { background: #f1f5f9; }
  .sg2-queue-nav:disabled { opacity: 0.3; cursor: not-allowed; }

  /* Inline Editing */
  .sg2-editable-text { cursor: pointer; display: inline-flex; align-items: center; gap: 6px; border-bottom: 1px dashed transparent; }
  .sg2-editable-text:hover { color: #0C4550; border-bottom-color: #0C4550; }
  .sg2-edit-icon { font-size: 11px; opacity: 0; color: #0C4550; transition: opacity 0.2s; }
  .sg2-editable-text:hover .sg2-edit-icon { opacity: 1; }
  .sg2-inline-edit-input { background: #111111; border: 1px solid #111111; color: #ffffff; border-radius: 6px; padding: 4px 8px; width: 100%; outline: none; box-shadow: 0 0 0 3px rgba(0,0,0,0.18); font-family: inherit; }
  .sg2-inline-edit-input:focus { border-color: #000000; box-shadow: 0 0 0 4px rgba(0,0,0,0.28); }
  .sg2-header-inline { font-size: 13px; font-weight: 600; color: #111827; }
  .sg2-editable-text.sg2-lead-name { font-size: 22px; font-weight: 700; color: #1a1f36; line-height: 1.2; }

  /* Modals */
  .sg2-inline-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; backdrop-filter: blur(2px); font-family: 'Plus Jakarta Sans', sans-serif; }
  .sg2-inline-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 420px; box-shadow: 0 20px 60px rgba(0,0,0,.18); overflow: hidden; margin: 0 16px; max-height: 90vh; overflow-y: auto; }
  .sg2-inline-header { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; font-weight: 700; color: #0C4550; position: sticky; top: 0; background: inherit; z-index: 1; }
  .sg2-inline-close { background: none; border: none; font-size: 16px; cursor: pointer; }
  .sg2-inline-body { padding: 16px 20px; display: flex; flex-direction: column; }
  .sg2-inline-label { font-size: 12px; font-weight: 600; color: #4b5563; margin-bottom: 4px; }
  .sg2-inline-select, .sg2-inline-textarea { background: #f8fafc; border: 1px solid #e5e9f0; border-radius: 8px; padding: 8px 12px; outline: none; width: 100%; box-sizing: border-box; font-family: inherit; }
  .sg2-inline-footer { padding: 12px 20px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 8px; position: sticky; bottom: 0; background: #fff; }
  .sg2-ghost-btn { background: #f8fafc; border: 1px solid #e5e9f0; padding: 8px 16px; border-radius: 8px; cursor: pointer; font-family: inherit; }
  .sg2-primary-btn { background: #0C4550; color: #fff; border: none; padding: 8px 20px; border-radius: 8px; cursor: pointer; font-family: inherit;}
  .sg2-primary-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ── Appointment wizard modal (identical to Sales God) ─────────────────── */
  .sg2-apt-modal-container { max-width: 640px; }
  .sg2-apt-steps { display: flex; align-items: center; gap: 8px; padding: 12px 20px 0; font-size: 12px; font-weight: 600; color: #9ca3af; }
  .sg2-apt-step { padding: 4px 10px; border-radius: 20px; background: #f1f5f9; }
  .sg2-apt-step--active { background: #0C4550; color: #fff; }
  .sg2-apt-step-sep { color: #cbd5e1; }
  .sg2-apt-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; padding: 6px 0 8px; }
  .sg2-apt-card { position: relative; text-align: left; cursor: pointer; font-family: inherit; background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 14px 14px 12px; transition: all .15s; }
  .sg2-apt-card:hover { background: #f1f5f9; border-color: #c7d2e8; transform: translateY(-1px); }
  .sg2-apt-card--selected { background: #e8f4f7; border-color: #0C4550; box-shadow: 0 0 0 2px rgba(12,69,80,.12); }
  .sg2-apt-card-title { font-size: 13px; font-weight: 700; color: #1a1f36; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sg2-apt-card-sub { font-size: 11.5px; color: #6b7280; margin-top: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sg2-apt-check { position: absolute; top: 8px; right: 8px; width: 18px; height: 18px; border-radius: 50%; background: #0C4550; color: #fff; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
  .sg2-apt-summary { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; font-size: 12.5px; color: #374151; display: flex; flex-direction: column; gap: 4px; }
  .sg2-apt-summary-label { font-weight: 700; color: #0C4550; margin-right: 4px; }
  @media (max-width: 640px) { .sg2-apt-grid { grid-template-columns: repeat(2, 1fr); } }

  /* ── Appointment search bar + "See All Departments" ──────────────── */
  .sg2-apt-search-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  .sg2-apt-search-icon {
    position: absolute;
    left: 10px;
    pointer-events: none;
  }
  .sg2-apt-search-input {
    width: 260px !important;
    padding: 7px 12px 7px 32px !important;
    font-size: 12.5px !important;
    margin: 0 !important;
    min-height: auto !important;
    border-radius: 20px !important;
  }
  @media (max-width: 640px) {
    .sg2-apt-search-input { width: 160px !important; }
  }
  .sg2-apt-alldept-btn {
    white-space: nowrap;
    background: #f3e8ff;
    border: 1px solid #e4d3fb;
    color: #673AB7;
    font-size: 12px;
    font-weight: 700;
    padding: 7px 14px;
    border-radius: 20px;
    cursor: pointer;
    font-family: inherit;
    transition: all .15s;
  }
  .sg2-apt-alldept-btn:hover { background: #ecdcff; border-color: #d9bffb; }
  .sg2-apt-alldept-btn--active { background: #0C4550; border-color: #0C4550; color: #fff; }

  /* ── "See All Departments" combined browsable list ────────────────── */
  .sg2-apt-alldept-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 4px 0 8px;
  }
  .sg2-apt-alldept-group {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 12px 14px;
  }
  .sg2-apt-alldept-dept-name {
    display: block;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    color: #0C4550;
    margin-bottom: 8px;
    padding: 0 0 6px;
    border-bottom: 1px solid #e5e9f0;
  }
  .sg2-apt-alldept-dept-loc {
    font-size: 11.5px;
    font-weight: 600;
    color: #6b7280;
  }

  .sg2-apt-new-btn { display: inline-flex; align-items: center; gap: 6px; background: #0C4550; color: #fff; border: none; border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; margin-bottom: 14px; transition: opacity .15s; }
  .sg2-apt-new-btn:hover { opacity: .9; }
  .sg2-apt-hist-list { display: flex; flex-direction: column; gap: 10px; }
  .sg2-apt-hist-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 16px; }
  .sg2-apt-hist-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
  .sg2-apt-hist-dept { font-size: 13px; font-weight: 700; color: #1a1f36; }
  .sg2-apt-hist-person { font-size: 12px; color: #6b7280; margin-top: 2px; }
  .sg2-apt-hist-meta { display: flex; gap: 14px; font-size: 12px; color: #4b5563; margin-top: 8px; font-weight: 500; }
  .sg2-apt-hist-notes { font-size: 12px; color: #374151; margin-top: 8px; background: #fff; border: 1px solid #e5e9f0; border-radius: 8px; padding: 8px 10px; }
  .sg2-apt-status-badge { font-size: 10.5px; font-weight: 700; padding: 3px 10px; border-radius: 20px; text-transform: uppercase; letter-spacing: .03em; white-space: nowrap; flex-shrink: 0; background: #e2e8f0; color: #475569; }
  .sg2-apt-status-badge--scheduled { background: #dbeafe; color: #1d4ed8; }
  .sg2-apt-status-badge--completed { background: #dcfce7; color: #16a34a; }
  .sg2-apt-status-badge--cancelled { background: #fee2e2; color: #dc2626; }
  .sg2-apt-status-badge--rescheduled { background: #fef3c7; color: #b45309; }
  .sg2-apt-status-badge--no_show { background: #f1f5f9; color: #64748b; }

  .sg2-history-empty { text-align: center; color: #9ca3af; font-size: 13px; padding: 24px 0; margin: 0; }

  .sg2-pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; padding: 4px 8px; font-size: 13px; font-weight: 600; color: #0C4550; }
  .sg2-pagination button { background: #0C4550; color: #fff; border: none; padding: 6px 16px; border-radius: 8px; cursor: pointer; font-family: inherit; font-size: 12px; font-weight: 600; transition: opacity 0.2s; }
  .sg2-pagination button:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Appointment modal pagination — Prev/Next kept close to the "Page X of Y" text */
  .sg2-apt-pagination {
    justify-content: center;
    gap: 16px;
    background: #f8fafc;
    border: 1px solid #e5e9f0;
    border-radius: 10px;
    padding: 8px 14px;
  }
  .sg2-apt-pagination-btns {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .sg2-apt-pagination-btns button {
    padding: 5px 12px;
    font-size: 11.5px;
  }

  /* SEARCH BAR IN HEADER (legacy, unused by the appointment modal now) */
  .sg2-header-search {
    background: #f8fafc;
    border: 1px solid #e5e9f0;
    border-radius: 20px;
    padding: 6px 12px;
    font-size: 12px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    color: #1a1f36;
    width: 180px;
    outline: none;
    transition: border-color .15s;
  }
  .sg2-header-search:focus { border-color: #0C4550; }
`;

export default IncomingMode;